import { useLocation } from "@tanstack/react-router";
import { Info, Star } from "lucide-react";
import * as React from "react";
import { useRegisterPageChrome } from "@/components/ui/page-chrome";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useFavorites } from "@/hooks/use-favorites";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
	title: string;
	description?: string;
	/** Override href used for favorites (defaults to current pathname) */
	href?: string;
	/** Actions slot rendered on the right side */
	actions?: React.ReactNode;
	children?: React.ReactNode;
	/**
	 * Tresc tooltipa pod ikonka info (literka "i" obok tytulu). Najczestsze
	 * uzycie: opisanie czestotliwosci odswiezania danych w raporcie ("co
	 * X minut", "raz dziennie o 03:00 UTC"). `null`/brak = ikona nie wyswietla
	 * sie. `TooltipProvider` jest globalnie w main.tsx — nie wymaga lokalnego.
	 */
	infoTooltip?: React.ReactNode;
}

export function PageHeader({
	title,
	description,
	href,
	actions,
	children,
	infoTooltip,
}: PageHeaderProps) {
	const pathname = useLocation({ select: (l) => l.pathname });
	const { isFavorite, addFavorite, removeFavorite } = useFavorites();

	const resolvedHref = href ?? pathname;
	const starred = isFavorite(resolvedHref);
	const isBelowMd = useIsBelowMd();
	// Telefon: tytul, ulubione i info przejmuje pasek gorny — tutaj zostaja
	// tylko akcje strony, zeby tytul nie powtarzal sie pod paskiem. Opis strony
	// (czesto z regulami, np. blokady edycji) trafia do ikony info w pasku.
	const topBarInfo = React.useMemo(() => {
		if (!description) return infoTooltip ?? undefined;
		if (infoTooltip == null) return description;
		return (
			<div className="flex flex-col gap-1.5">
				<p>{description}</p>
				<div>{infoTooltip}</div>
			</div>
		);
	}, [description, infoTooltip]);
	const inTopBar = useRegisterPageChrome(
		{ title, favoriteHref: resolvedHref, infoTooltip: topBarInfo },
		isBelowMd,
	);

	const toggleFavorite = React.useCallback(() => {
		if (starred) {
			removeFavorite(resolvedHref);
		} else {
			addFavorite({ href: resolvedHref, title });
		}
	}, [starred, resolvedHref, title, addFavorite, removeFavorite]);

	if (inTopBar) {
		const pageActions = actions ?? children;
		return pageActions ? (
			<div className="flex flex-wrap items-center gap-2" data-testid="page-header-actions">
				{pageActions}
			</div>
		) : null;
	}

	return (
		<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex items-center gap-3 min-w-0">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">{title}</h1>
						<button
							type="button"
							onClick={toggleFavorite}
							aria-label={starred ? "Usuń z ulubionych" : "Dodaj do ulubionych"}
							className={cn(
								"flex size-7 shrink-0 items-center justify-center rounded-md transition-colors",
								"hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
								starred ? "text-primary" : "text-muted-foreground/40 hover:text-muted-foreground",
							)}
						>
							<Star className={cn("size-4 transition-all", starred && "fill-primary")} />
						</button>
						{infoTooltip != null && (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										aria-label="Informacje o raporcie"
										className={cn(
											"flex size-7 shrink-0 items-center justify-center rounded-md transition-colors",
											"text-muted-foreground/60 hover:bg-accent hover:text-muted-foreground",
											"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
										)}
										data-testid="page-header-info"
									>
										<Info className="size-4" />
									</button>
								</TooltipTrigger>
								<TooltipContent
									side="bottom"
									align="start"
									className="max-w-xs text-xs leading-relaxed"
									data-testid="page-header-info-tooltip"
								>
									{infoTooltip}
								</TooltipContent>
							</Tooltip>
						)}
					</div>
					{description && (
						<p className="hidden sm:block text-sm text-muted-foreground">{description}</p>
					)}
				</div>
			</div>
			{(actions || children) && (
				<div className="flex items-center gap-2 flex-wrap">{actions ?? children}</div>
			)}
		</div>
	);
}
